import { Box, Chip } from '@mui/material';
import { COLORS } from '../../Styles/Colors';

type ConnectionStatus = 'online' | 'offline' | 'delayed' | 'unknown' | string;
type MachineStatus = 'healthy' | 'attention' | 'action_required' | 'critical' | 'unknown' | string;

const CONNECTION_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  online: { bg: '#e8f5e9', color: '#2e7d32', label: 'Online' },
  offline: { bg: '#ffebee', color: COLORS.red, label: 'Offline' },
  delayed: { bg: '#fff3e0', color: COLORS.orange, label: 'Delayed' },
  unknown: { bg: '#f5f5f5', color: COLORS.gray, label: 'Unknown' },
};

const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  healthy: { bg: '#e8f5e9', color: '#2e7d32', label: 'Healthy' },
  attention: { bg: '#fff8e1', color: '#f9a825', label: 'Attention' },
  action_required: { bg: '#fff3e0', color: COLORS.orange, label: 'Action Required' },
  critical: { bg: '#ffebee', color: COLORS.red, label: 'Critical' },
  unknown: { bg: '#f5f5f5', color: COLORS.gray, label: 'Unknown' },
};

export const ConnectionStatusChip = ({ status }: { status: ConnectionStatus }) => {
  const config = CONNECTION_COLORS[status] || CONNECTION_COLORS.unknown;
  return (
    <Chip
      label={config.label}
      size="small"
      sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 600 }}
    />
  );
};

export const MachineStatusChip = ({ status }: { status: MachineStatus }) => {
  const config = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <Chip
      label={config.label}
      size="small"
      sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 600 }}
    />
  );
};

export type MachineListStatus = 'online' | 'alert' | 'offline';

export const getMachineListStatus = (params: {
  connection_status: string;
  status: string;
  open_alerts_count?: number;
  empty_slots?: number;
  low_slots?: number;
}): MachineListStatus => {
  if (params.connection_status === 'offline') return 'offline';

  if (
    params.connection_status === 'delayed'
    || params.status === 'attention'
    || params.status === 'action_required'
    || params.status === 'critical'
    || (params.open_alerts_count ?? 0) > 0
    || (params.empty_slots ?? 0) > 0
    || (params.low_slots ?? 0) > 0
  ) {
    return 'alert';
  }

  return 'online';
};

const LIST_STATUS_COLORS: Record<MachineListStatus, { dot: string; color: string; label: string }> = {
  online: { dot: '#2e7d32', color: '#2e7d32', label: 'Online' },
  alert: { dot: '#f9a825', color: '#f57c00', label: 'Alert' },
  offline: { dot: COLORS.red, color: COLORS.red, label: 'Offline' },
};

export const MachineListStatusIndicator = ({
  connection_status,
  status,
  open_alerts_count,
  empty_slots,
  low_slots,
}: {
  connection_status: string;
  status: string;
  open_alerts_count?: number;
  empty_slots?: number;
  low_slots?: number;
}) => {
  const listStatus = getMachineListStatus({
    connection_status,
    status,
    open_alerts_count,
    empty_slots,
    low_slots,
  });
  const config = LIST_STATUS_COLORS[listStatus];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: config.dot,
          flexShrink: 0,
        }}
      />
      <Box component="span" sx={{ color: config.color, fontWeight: 600, fontSize: '0.8125rem' }}>
        {config.label}
      </Box>
    </Box>
  );
};

export const StockLevelChip = ({ level }: { level: string }) => {
  const map: Record<string, { bg: string; color: string }> = {
    good: { bg: '#e8f5e9', color: '#2e7d32' },
    low: { bg: '#fff3e0', color: COLORS.orange },
    empty: { bg: '#ffebee', color: COLORS.red },
    unknown: { bg: '#f5f5f5', color: COLORS.gray },
  };
  const config = map[level] || map.unknown;
  return (
    <Chip
      label={level}
      size="small"
      sx={{ backgroundColor: config.bg, color: config.color, fontWeight: 600, textTransform: 'capitalize' }}
    />
  );
};
